import { TitleCasePipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { filter, switchMap, take, tap } from 'rxjs/operators';
import { DsModalRef, DsModalService, DsModalVariant } from '@levelaccess/design-system';

import { IAutomatedScanIssue, IScanIssues, IScanIssuesSuccessCriteria } from '@shared/interfaces/scan-issues.interface';
import { ITask } from '@shared/interfaces/task.interface';
import { IDismissRestoreRequest } from '@shared/interfaces/ignore.interface';
import { IDigitalPropertyListItem } from '@shared/interfaces/digital-property.interface';
import { ISuccessCriteria } from '@shared/audits/definitions/success-criteria/success-criteria.interface';
import { AuditTypes } from '@shared/constants/audit-types';
import { $digitalProperty } from '@shared/constants/digital-properties';
import { RuleAuditHistoryAction } from '@shared/constants/rule-audit-history';
import { $scanIssues } from '@shared/constants/scan-issues';
import { $auditStandard, mapAuditDbStandardToAuditStandard } from '@shared/constants/audit-standard';
import { $auditStandardSuccessCriteria } from '@shared/constants/audit-standard-success-criteria';
import { AuditToolNameToToolCommonName, mapAuditToolCommonNamesToDisplayName } from '@shared/constants/audit-tool';
import { SharedCommonUtility } from '@shared/utils/common.utility';
import { MarkdownUtility } from '@shared/utils/markdown.utility';
import { ScanReportService } from '@client/app/services/scan-report.service';
import { UserService } from '@client/app/services/user.service';
import { SuccessCriteriaService } from '@client/app/services/success-criteria.service';
import { TranslateService } from '@client/app/translate/translate.service';
import { IssuesBulkActionModalComponent } from '@client/app/pages/public/scans/scan-results/issues-view/issues-bulk-action-modal/issues-bulk-action-modal.component';
import { UpsertTaskSlideoutComponent } from '@client/app/pages/public/projects/tasks/upsert-task-slideout/upsert-task-slideout.component';
import {
  ICreateTaskDescription,
  JiraMarkdownUtils,
} from '@client/app/components/markdown-editor/markdown-formatter/markdown-jira.utility';

export interface IIssueActionContext {
  auditType: AuditTypes;
  ruleDetails: IScanIssues;
  scanId: string;
  scanUrl: string;
}

@Injectable({ providedIn: 'root' })
export class IssueActionsService {
  private readonly titleCasePipe: TitleCasePipe = new TitleCasePipe();

  constructor(
    private dsModalService: DsModalService,
    private scanReportService: ScanReportService,
    private userService: UserService,
    private successCriteriaService: SuccessCriteriaService,
    private translateService: TranslateService,
  ) {}

  private getIgnoreIssuesRequest(
    issues: IAutomatedScanIssue[],
    reason: string,
    context: IIssueActionContext,
  ): IDismissRestoreRequest {
    const matchedFlaws: string[] = [];
    const unmatchedFlaws: string[] = [];

    issues.forEach((issue: IAutomatedScanIssue): void => {
      if (issue.isRawFinding) {
        unmatchedFlaws.push(issue.rawFindingId);
      } else {
        matchedFlaws.push(issue._id);
      }
    });

    return {
      auditType: context.auditType,
      ruleId: context.ruleDetails[$scanIssues.ruleId],
      reason,
      scanId: context.scanId,
      unmatchedFlaws,
      flaws: matchedFlaws,
    };
  }

  private createDefinitionLink(def: ISuccessCriteria): string {
    const { num, handle, level, url } = def;
    return `[${num} ${handle} - Level ${level}](${url})`;
  }

  public getCreateTaskDescription(ruleDetails: IScanIssues): string {
    const getWCAGSuccessCriteria = (listOfSuccessCriteria: IScanIssuesSuccessCriteria[]): string => {
      const uniqueSuccessCriteria: Set<string> = new Set<string>();
      for (const successCriteria of listOfSuccessCriteria) {
        for (const standard of successCriteria[$auditStandardSuccessCriteria.auditStandards]) {
          const definition: ISuccessCriteria = this.successCriteriaService.getSuccessCriteriaFromStandard(
            mapAuditDbStandardToAuditStandard[standard[$auditStandard.standard]],
            successCriteria[$auditStandardSuccessCriteria.identifier],
          );
          if (definition !== null) {
            uniqueSuccessCriteria.add(this.createDefinitionLink(definition));
          }
        }
      }
      return [...uniqueSuccessCriteria].join(MarkdownUtility.br);
    };

    const config: ICreateTaskDescription[] = [
      { header: 'table_header_rule_description', content: ruleDetails[$scanIssues.description] },
      {
        header: 'tool_name',
        content: this.translateService.instant(
          mapAuditToolCommonNamesToDisplayName[AuditToolNameToToolCommonName[ruleDetails[$scanIssues.toolName]]],
        ),
      },
      { header: 'issue_field_severity', content: this.titleCasePipe.transform(ruleDetails[$scanIssues.severity]) },
      { header: 'issue_field_successCriteria', content: getWCAGSuccessCriteria(ruleDetails[$scanIssues.successCriteria]) },
      { header: 'audit_rule_property_why_it_matters', content: ruleDetails[$scanIssues.whyItMatters] },
      { header: 'audit_rule_property_what_to_do', content: ruleDetails[$scanIssues.whatToDo] },
    ];

    return config
      .map((d: ICreateTaskDescription): string => JiraMarkdownUtils.jiraDescriptionBlock(this.translateService, d))
      .join('');
  }

  public openDismissOrRestoreModal(
    issues: IAutomatedScanIssue[],
    actionType: RuleAuditHistoryAction,
    context: IIssueActionContext,
  ): Observable<number> {
    const modalRef: DsModalRef = this.dsModalService.open(IssuesBulkActionModalComponent, DsModalVariant.center);
    if (SharedCommonUtility.isNullish(modalRef)) {
      return EMPTY;
    }

    const instance: IssuesBulkActionModalComponent = modalRef.componentInstance;
    instance.actionType = actionType;
    instance.modalTitle = `rule_action_modal_description_selected_issues_${actionType}`;
    instance.ruleTitle = context.ruleDetails[$scanIssues.title];

    return instance.container.onClose$.pipe(
      take(1),
      tap((): void => {
        this.dsModalService.closeCurrentModal();
      }),
      filter((): boolean => instance.confirmed),
      switchMap((): Observable<number> => {
        const request: IDismissRestoreRequest = this.getIgnoreIssuesRequest(issues, instance.reason, context);
        return actionType === RuleAuditHistoryAction.dismiss
          ? this.scanReportService.dismissIssues(request)
          : this.scanReportService.restoreIssues(request);
      }),
    );
  }

  public openCreateTaskSlideout(issues: IAutomatedScanIssue[], context: IIssueActionContext): Observable<ITask> {
    return this.userService.currentDigitalProperty$.pipe(
      take(1),
      switchMap((digitalPropertyListItem: IDigitalPropertyListItem): Observable<ITask> => {
        const modalRef: DsModalRef = this.dsModalService.open(UpsertTaskSlideoutComponent, DsModalVariant.slideOut);
        if (SharedCommonUtility.isNullish(modalRef)) {
          return EMPTY;
        }

        const createTaskModalRef: UpsertTaskSlideoutComponent = modalRef.componentInstance;
        createTaskModalRef.taskName = context.ruleDetails[$scanIssues.title];
        createTaskModalRef.description = this.getCreateTaskDescription(context.ruleDetails);
        createTaskModalRef.issues = context.ruleDetails;
        createTaskModalRef.selectedIssues = issues;
        createTaskModalRef.workspaceId = digitalPropertyListItem[$digitalProperty.workspace]._id;
        createTaskModalRef.digitalPropertyId = digitalPropertyListItem[$digitalProperty._id];
        createTaskModalRef.scanId = context.scanId;
        createTaskModalRef.scanUrl = context.scanUrl;
        createTaskModalRef.toolName = context.ruleDetails[$scanIssues.toolName];
        createTaskModalRef.modalInit(true);

        return createTaskModalRef.onUpsert.pipe(take(1));
      }),
    );
  }
}
